// Compares each result against the same athlete's previous result of the same
// workout, and marks their fastest one.
//
// Two results are comparable only when the boat class, result type and the
// piece structure all match. Structure means the ordered list of piece
// distances for distance-based work, and piece times for time-based work — so
// 4x2k and 2x4k are different workouts despite both totalling 8000m, and a
// standalone 2k is different again. Individual pieces inside a result are never
// compared with anything; only whole results are.
//
// Safe to compute on the client: every filter the results list applies
// (athlete, boat class, result type) is a comparability dimension, so a group
// is either fully loaded or absent entirely — never truncated. The list
// endpoints are unpaginated, so the full history is present.

import { parseResultDate, splitFor } from '../components/analytics/chartUtils';

const parseDetails = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Ordered piece sizes: metres for distance-based work, seconds for time-based.
// Returns null when the result carries no usable total.
const structureSignature = (row) => {
  const timeBased = row.workout_mode === 'time';
  const total = Number(timeBased ? row.total_time_seconds : row.distance_meters);
  if (!Number.isFinite(total) || total <= 0) return null;

  const count = Math.max(1, parseInt(row.interval_count, 10) || 1);
  const details = parseDetails(row.interval_details);
  const evenPiece = total / count;

  const pieces =
    details.length > 0
      ? details.map((piece) => {
          const value = Number(timeBased ? piece?.time_seconds : piece?.distance_meters);
          return Number.isFinite(value) && value > 0 ? value : evenPiece;
        })
      : Array.from({ length: count }, () => evenPiece);

  return pieces.map((piece) => Math.round(piece)).join('x');
};

const groupKey = (row) => {
  const structure = structureSignature(row);
  if (structure === null) return null;
  const athlete = row.athlete_id ?? 'self';
  const boat = row.boat_class || 'Unknown';
  const type = row.result_type || 'workout';
  const mode = row.workout_mode === 'time' ? 't' : 'd';
  return `${athlete}::${boat}::${type}::${mode}::${structure}`;
};

// result_date only has day precision, so fall back to insertion order for
// several results logged on the same day.
const tieBreak = (a, b) => {
  const aCreated = Date.parse(a.row.created_at || '') || 0;
  const bCreated = Date.parse(b.row.created_at || '') || 0;
  return aCreated - bCreated || (a.row.id || 0) - (b.row.id || 0);
};

/**
 * @returns Map keyed by result id -> {
 *   previous, previousDate, comparableCount, isBest,
 *   deltaSeconds,          // total-time change vs. previous (distance-based work)
 *   deltaSplitSeconds,     // per-500m change vs. previous (always meaningful)
 *   personalBest,          // the PB row, or null when this result is the PB
 *   personalBestDate,
 *   deltaToBestSeconds,    // total-time gap to the PB
 *   deltaToBestSplitSeconds,
 *   compareOn              // 'time' | 'split' — which delta to show
 * }
 */
export function buildResultComparisons(results) {
  const index = new Map();
  if (!Array.isArray(results) || results.length === 0) return index;

  const groups = new Map();
  for (const row of results) {
    if (row?.id == null) continue;
    const key = groupKey(row);
    if (key === null) continue;
    const split = splitFor(row);
    const dateMs = parseResultDate(row.result_date);
    if (split == null || split <= 0 || dateMs == null) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ row, split, dateMs });
  }

  for (const entries of groups.values()) {
    entries.sort((a, b) => a.dateMs - b.dateMs || tieBreak(a, b));

    // Entries are in date order, so a strict comparison keeps the first
    // occasion the athlete hit the time — a later result that merely equals it
    // has matched the PB, not set a new one.
    const best = entries.reduce((b, e) => (e.split < b.split ? e : b), entries[0]);
    const bestTime = Number(best.row.total_time_seconds);

    entries.forEach((entry, i) => {
      const prev = i > 0 ? entries[i - 1] : null;
      const prevTime = prev ? Number(prev.row.total_time_seconds) : null;
      const time = Number(entry.row.total_time_seconds);
      const isBest = entries.length > 1 && entry === best;

      index.set(entry.row.id, {
        comparableCount: entries.length,
        previous: prev ? prev.row : null,
        previousDate: prev ? prev.row.result_date : null,
        isBest,
        deltaSeconds:
          prev && Number.isFinite(prevTime) && Number.isFinite(time) ? time - prevTime : null,
        deltaSplitSeconds: prev ? entry.split - prev.split : null,
        // Personal best for this workout. Null on the PB row itself, which
        // shows the Best badge instead.
        personalBest: isBest ? null : best.row,
        personalBestDate: isBest ? null : best.row.result_date,
        deltaToBestSeconds:
          !isBest && entry !== best && Number.isFinite(bestTime) && Number.isFinite(time)
            ? time - bestTime
            : null,
        deltaToBestSplitSeconds: !isBest && entry !== best ? entry.split - best.split : null,
        // Time-based work holds time constant and varies distance, so only the
        // split delta says anything; distance is fixed for everything else.
        compareOn: entry.row.workout_mode === 'time' ? 'split' : 'time',
      });
    });
  }

  return index;
}
